import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  getOobHome,
  getOobAnnouncements,
  getOobContactSettings,
  updateOobContactSettings,
  sendOobContactVerificationCode,
  verifyOobContactVerificationCode,
  OobHomeEventSummaryDto,
  OobUserHomeDto,
  OobPlatformAnnouncementDto,
  OobContactSettingsDto,
} from "@/api/outOfBandApi";

const fmt = (d?: string) => (d ? new Date(d).toLocaleString() : "-");

const OobUserHome: React.FC = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [home, setHome] = useState<OobUserHomeDto | null>(null);
  const [announcements, setAnnouncements] = useState<OobPlatformAnnouncementDto[]>([]);
  const [announcementSearch, setAnnouncementSearch] = useState("");
  const [eventSearch, setEventSearch] = useState("");
  const [contactSettings, setContactSettings] = useState<OobContactSettingsDto | null>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [personalPhone, setPersonalPhone] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState<"Email" | "Text">("Email");
  const [emailCode, setEmailCode] = useState("");
  const [textCode, setTextCode] = useState("");
  
  // Check if user is logged in
  const isLoggedIn = accounts && accounts.length > 0;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let homeData: OobUserHomeDto | null = null;
        let announcementsData: any = { announcements: [] };
        
        // If logged in, try to load M365 events
        if (isLoggedIn) {
          try {
            homeData = await getOobHome();
            console.log("Home data:", homeData);
          } catch (err: any) {
            console.error("Error loading M365 home:", err);
            // Only show error if logged in
            const apiCode = err?.response?.data?.code;
            if (apiCode !== "OOB_ACCESS_REQUIRED") {
              throw err;
            }
            homeData = null;
          }
        }
        
        // Always try to load announcements
        try {
          announcementsData = await getOobAnnouncements(0, 100);
          console.log("Announcements data:", announcementsData);
        } catch (err: any) {
          console.error("Error loading announcements:", err);
          announcementsData = { announcements: [] };
        }
        
        setHome(homeData);
        setAnnouncements(announcementsData.announcements || []);

        if (isLoggedIn) {
          try {
            const settings = await getOobContactSettings();
            setContactSettings(settings);
            setPersonalPhone(settings.personalPhone || "");
            setPersonalEmail(settings.personalEmail || "");
            setPreferredContactMethod(settings.preferredContactMethod);
          } catch {
            setContactSettings(null);
          }
        }
      } catch (err: any) {
        console.error("Error loading OOB content:", err);
        setError(
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load content."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isLoggedIn, navigate]);

  const reloadContactSettings = async () => {
    const latest = await getOobContactSettings();
    setContactSettings(latest);
    setPersonalPhone(latest.personalPhone || "");
    setPersonalEmail(latest.personalEmail || "");
    setPreferredContactMethod(latest.preferredContactMethod);
  };

  const handleSaveContactSettings = async () => {
    try {
      setSettingsLoading(true);
      setSettingsError(null);
      setSettingsMessage(null);
      const updated = await updateOobContactSettings({ personalPhone, personalEmail, preferredContactMethod });
      setContactSettings(updated);
      setSettingsMessage("Contact settings saved. Verify both Email and Text to complete onboarding.");
    } catch (err: any) {
      setSettingsError(err?.response?.data?.error || "Failed to save contact settings.");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleSendCode = async (channel: "Email" | "Text") => {
    try {
      setSettingsLoading(true);
      setSettingsError(null);
      setSettingsMessage(null);
      const result = await sendOobContactVerificationCode({ channel });
      setSettingsMessage(result.message || `${channel} verification code sent.`);
    } catch (err: any) {
      setSettingsError(err?.response?.data?.error || `Failed to send ${channel} verification code.`);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleVerifyCode = async (channel: "Email" | "Text") => {
    try {
      setSettingsLoading(true);
      setSettingsError(null);
      setSettingsMessage(null);
      const code = channel === "Email" ? emailCode : textCode;
      const result = await verifyOobContactVerificationCode({ channel, code });
      if (result.settings) {
        setContactSettings(result.settings);
      } else {
        await reloadContactSettings();
      }
      if (channel === "Email") {
        setEmailCode("");
      } else {
        setTextCode("");
      }
      setSettingsMessage(result.message || `${channel} verification completed.`);
    } catch (err: any) {
      setSettingsError(err?.response?.data?.error || `Failed to verify ${channel} code.`);
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleEventClick = (event: OobHomeEventSummaryDto) => {
    navigate(`/oob/${event.eventGuid}`);
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading OOB Portal...</p>
      </Container>
    );
  }

  return (
    <Container fluid={isLoggedIn} className={isLoggedIn ? "py-4" : "py-5"}>
      {/* Header - Logged In Only */}
      {isLoggedIn && (
        <Row className="mb-4">
          <Col>
            <div className="border-bottom pb-3 d-flex justify-content-between align-items-start gap-3 flex-wrap">
              <div>
              <h1 className="mb-2">{home?.title || "Out of Band Events"}</h1>
              <p className="text-muted mb-0">{home?.subtitle || "Manage your out of band events"}</p>
              </div>
              <Button variant="outline-secondary" size="sm" onClick={() => setShowSettingsModal(true)}>
                Contact Settings
              </Button>
            </div>
          </Col>
        </Row>
      )}

      {isLoggedIn && error && <Alert variant="danger" className="mb-4">{error}</Alert>}

      {isLoggedIn && contactSettings && !contactSettings.isOnboardingComplete && (
        <Alert variant="warning" className="mb-4">
          <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <strong>Onboarding Incomplete:</strong> Verify both Email and Text, then choose your preferred method.
            </div>
            <Button size="sm" variant="outline-dark" onClick={() => setShowSettingsModal(true)}>
              Contact Settings
            </Button>
          </div>
        </Alert>
      )}

      {isLoggedIn && contactSettings && contactSettings.isOnboardingComplete && (
        <Alert variant="success" className="mb-4">
          Contact methods verified. Preferred method: <strong>{contactSettings.preferredContactMethod}</strong>.
          <Button size="sm" className="ms-3" variant="outline-success" onClick={() => setShowSettingsModal(true)}>
            Update Settings
          </Button>
        </Alert>
      )}

      {isLoggedIn && contactSettings && contactSettings.isOnboardingComplete &&
        contactSettings.secondaryIdentityStatus !== "Ready" && (
        <Alert variant="warning" className="mb-4">
          <strong>Secondary Tenant access is not ready.</strong>{" "}
          Your contact methods are verified, but your backup Microsoft 365 identity still needs to be confirmed.
          An administrator can resolve an existing guest identity or start the fallback invitation process.
          {contactSettings.secondaryIdentityLastError && (
            <div className="small mt-2">Latest status: {contactSettings.secondaryIdentityLastError}</div>
          )}
        </Alert>
      )}

      {isLoggedIn && contactSettings?.secondaryIdentityStatus === "Ready" && (
        <Alert variant="info" className="mb-4">
          Secondary Tenant identity verified. You can be added to event Teams when required.
        </Alert>
      )}

      {/* Show Announcements Only if Not Logged In */}
      {!isLoggedIn && (
        <>
          <div className="mb-4">            <h1>Platform Updates</h1>            <p className="text-muted">
              Important announcements and updates for emergency access participants
            </p>
          </div>

          <Row className="mb-4">
            <Col md={8}>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search announcements..."
                  value={announcementSearch}
                  onChange={(e) => setAnnouncementSearch(e.target.value)}
                />
              </div>
            </Col>
            <Col md={4} className="text-end">
              <Button
                variant="primary"
                onClick={() => navigate("/external")}
              >
                Access an Event
              </Button>
            </Col>
          </Row>

          {error && <Alert variant="danger">{error}</Alert>}

          {announcements.length === 0 && (
            <Alert variant="info">
              No announcements available at this time.
            </Alert>
          )}

          <Row>
            {announcements
              .filter((a) =>
                announcementSearch === "" ||
                a.title.toLowerCase().includes(announcementSearch.toLowerCase()) ||
                a.message.toLowerCase().includes(announcementSearch.toLowerCase())
              )
              .map((announcement) => (
                <Col md={12} key={announcement.announcementGuid} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title className="mb-2">{announcement.title}</Card.Title>
                      <Card.Text className="text-muted small mb-2">
                        {fmt(announcement.createDt)} • From: {announcement.createdBy}
                      </Card.Text>
                      <Card.Text>{announcement.message}</Card.Text>
                      {announcement.recipientCount > 0 && (
                        <Card.Text className="text-muted small">
                          Recipients: {announcement.recipientCount} • Queued: {announcement.queuedCount}
                        </Card.Text>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            
            {announcements.filter((a) =>
              announcementSearch === "" ||
              a.title.toLowerCase().includes(announcementSearch.toLowerCase()) ||
              a.message.toLowerCase().includes(announcementSearch.toLowerCase())
            ).length === 0 && announcementSearch && (
              <Col md={12}>
                <Alert variant="info">
                  No announcements match your search.
                </Alert>
              </Col>
            )}
          </Row>
        </>
      )}

      {/* Show M365 Events if Logged In */}
      {isLoggedIn && home && (
        <Row>
          <Col>
            <Tabs defaultActiveKey="communications" className="mb-3">
              {/* Active Communications Tab */}
              <Tab eventKey="communications" title={`Active Communications (${announcements.length + (home?.totalActiveEvents || 0)})`}>
              <div className="py-3">
                <Row className="g-3">
                  {/* Left Column: Announcements */}
                  <Col lg={6}>
                    <Card className="h-100">
                      <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <div>
                          <Badge bg="primary" className="me-2">Announcements</Badge>
                          <span className="small text-muted">({announcements.length})</span>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-3" style={{ maxHeight: "600px", overflowY: "auto" }}>
                        <div className="mb-3">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search announcements..."
                            value={announcementSearch}
                            onChange={(e) => setAnnouncementSearch(e.target.value)}
                          />
                        </div>

                        {announcements
                          .filter((a) =>
                            announcementSearch === "" ||
                            a.title.toLowerCase().includes(announcementSearch.toLowerCase()) ||
                            a.message.toLowerCase().includes(announcementSearch.toLowerCase())
                          )
                          .map((announcement) => (
                            <Card key={announcement.announcementGuid} className="mb-2 border-primary border-start border-5">
                              <Card.Body className="p-2">
                                <h6 className="mb-2 fw-bold small">{announcement.title}</h6>
                                <p className="mb-2 small text-break">{announcement.message.substring(0, 150)}{announcement.message.length > 150 ? "..." : ""}</p>
                                <div className="small text-muted">
                                  <div>From: <strong>{announcement.createdBy}</strong></div>
                                  <div>{fmt(announcement.createDt)}</div>
                                  <Badge bg="secondary" className="mt-1">{announcement.recipientCount} recipients</Badge>
                                </div>
                              </Card.Body>
                            </Card>
                          ))}

                        {announcements.filter((a) =>
                          announcementSearch === "" ||
                          a.title.toLowerCase().includes(announcementSearch.toLowerCase()) ||
                          a.message.toLowerCase().includes(announcementSearch.toLowerCase())
                        ).length === 0 && (
                          <Alert variant="light" className="mb-0">
                            No announcements {announcementSearch ? "matching your search" : "at this time"}.
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Right Column: Active Events */}
                  <Col lg={6}>
                    <Card className="h-100">
                      <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                        <div>
                          <Badge bg="warning" className="me-2">Active Events</Badge>
                          <span className="small text-muted">({home?.activeEvents.length || 0})</span>
                        </div>
                      </Card.Header>
                      <Card.Body className="p-3" style={{ maxHeight: "600px", overflowY: "auto" }}>
                        <div className="mb-3">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search events..."
                            value={eventSearch}
                            onChange={(e) => setEventSearch(e.target.value)}
                          />
                        </div>

                        {home && home.activeEvents.length > 0 ? (
                          <div>
                            {home.activeEvents
                              .filter((e) =>
                                eventSearch === "" ||
                                e.eventName.toLowerCase().includes(eventSearch.toLowerCase()) ||
                                e.eventType.toLowerCase().includes(eventSearch.toLowerCase())
                              )
                              .map((event) => (
                                <Card key={event.eventGuid} className="mb-2">
                                  <Card.Body className="p-2">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                      <h6 className="mb-0 fw-bold small">{event.eventName}</h6>
                                      <Badge bg={getBadgeColor(event.eventType)}>
                                        {event.eventType}
                                      </Badge>
                                    </div>
                                    <div className="small text-muted mb-2">
                                      <div>Activated: {fmt(event.activatedDt)}</div>
                                      <div>Updates: <Badge bg="secondary">{event.updateCount}</Badge></div>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      className="w-100"
                                      onClick={() => handleEventClick(event)}
                                    >
                                      View Details
                                    </Button>
                                  </Card.Body>
                                </Card>
                              ))}

                            {home.activeEvents.filter((e) =>
                              eventSearch === "" ||
                              e.eventName.toLowerCase().includes(eventSearch.toLowerCase()) ||
                              e.eventType.toLowerCase().includes(eventSearch.toLowerCase())
                            ).length === 0 && (
                              <Alert variant="light" className="mb-0">
                                No events matching your search.
                              </Alert>
                            )}
                          </div>
                        ) : (
                          <Alert variant="info" className="mb-0">
                            No active events at this time.
                          </Alert>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                {/* Combined Status */}
                <Row className="mt-3">
                  <Col>
                    {announcements.length === 0 && (!home || home.activeEvents.length === 0) && (
                      <Alert variant="success" className="mb-0">
                        <strong>✓ All Clear</strong> — No active announcements or events at this time. Stay tuned for updates.
                      </Alert>
                    )}
                  </Col>
                </Row>
              </div>
            </Tab>

            {/* About & FAQ Tab */}
            <Tab eventKey="about" title="About & FAQ">
              <div className="py-3">
                <Row className="g-4">
                  <Col lg={6}>
                    <Card>
                      <Card.Header className="bg-light">
                        <Card.Title className="mb-0">About OOB Communications</Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <p>
                          The <strong>Out-of-Band Communications (OOB)</strong> system provides critical updates when standard 
                          communication channels like Microsoft Teams or email are unavailable or unreliable.
                        </p>
                        <h6 className="mb-2">How It Works</h6>
                        <ul className="small">
                          <li>Admins send notifications via email or SMS</li>
                          <li>You receive a link to this status board</li>
                          <li>View real-time updates and instructions</li>
                          <li>Acknowledge receipt of critical messages</li>
                        </ul>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={6}>
                    <Card>
                      <Card.Header className="bg-light">
                        <Card.Title className="mb-0">Frequently Asked Questions</Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <h6>Why receive notifications?</h6>
                        <p className="small mb-3">
                          You have been added to an OOB group to ensure critical information reaches you during emergencies 
                          or incidents affecting standard communication channels.
                        </p>

                        <h6>What should I do if I receive a notification?</h6>
                        <p className="small mb-3">
                          Click the link in your email or SMS to access this portal, review the latest updates, and acknowledge receipt.
                        </p>

                        <h6>How often will I receive notifications?</h6>
                        <p className="small mb-3">
                          You may receive periodic test notifications to ensure your contact information is current. 
                          Notifications will only increase frequency during actual incidents.
                        </p>

                        <h6>How do I update my contact information?</h6>
                        <p className="small">
                          From the onboarding email, you can update your phone and alternate email. Keep this information current.
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="g-4 mt-1">
                  <Col lg={12}>
                    <Card>
                      <Card.Header className="bg-light">
                        <Card.Title className="mb-0">For More Information</Card.Title>
                      </Card.Header>
                      <Card.Body>
                        <p className="small mb-0">
                          If you have questions about OOB Communications or need to update your contact information, 
                          please contact your IT department or security team.
                        </p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </div>
            </Tab>
          </Tabs>

          {/* Footer */}
          <Row className="mt-4">
            <Col>
              <div className="text-muted small text-center border-top pt-3">
                <p className="mb-0">
                  Last updated: {home && fmt(home.generatedDt)}
                </p>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
      )}

      <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Contact Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {settingsError && <Alert variant="danger">{settingsError}</Alert>}
          {settingsMessage && <Alert variant="info">{settingsMessage}</Alert>}

          <Alert variant="warning">
            <strong>Demo Note:</strong> Text verification is simulated by email with subject prefix <strong>TEXTMSG</strong>.
          </Alert>

          <Form.Group className="mb-3">
            <Form.Label>Work Email</Form.Label>
            <Form.Control value={contactSettings?.email || ""} disabled />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Personal Email (required)</Form.Label>
            <Form.Control
              value={personalEmail}
              onChange={(e) => setPersonalEmail(e.target.value)}
              placeholder="e.g. you@personal-email.com"
              disabled={settingsLoading}
            />
            <Form.Text className="text-muted">
              Used for out-of-band notifications and Secondary Tenant access. Changing this requires reverification.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Mobile Number (required)</Form.Label>
            <Form.Control
              value={personalPhone}
              onChange={(e) => setPersonalPhone(e.target.value)}
              placeholder="e.g. +1 555 0100"
              disabled={settingsLoading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Preferred Contact Method</Form.Label>
            <Form.Select
              value={preferredContactMethod}
              onChange={(e) => setPreferredContactMethod(e.target.value as "Email" | "Text")}
              disabled={settingsLoading}
            >
              <option value="Email">Email</option>
              <option value="Text">Text</option>
            </Form.Select>
          </Form.Group>

          <div className="d-grid mb-3">
            <Button onClick={handleSaveContactSettings} disabled={settingsLoading}>
              Save Contact Settings
            </Button>
          </div>

          <Card className="mb-3">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <strong>Email Verification</strong>
                  <div className="small text-muted">Status: {contactSettings?.personalEmailVerified ? "Verified" : "Not Verified"}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => handleSendCode("Email")}
                  disabled={settingsLoading || !personalEmail}
                >
                  Send Code
                </Button>
              </div>
              <div className="d-flex gap-2">
                <Form.Control
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  disabled={settingsLoading}
                />
                <Button size="sm" onClick={() => handleVerifyCode("Email")} disabled={settingsLoading || emailCode.length !== 6}>
                  Verify
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <strong>Text Verification</strong>
                  <div className="small text-muted">Status: {contactSettings?.personalPhoneVerified ? "Verified" : "Not Verified"}</div>
                </div>
                <Button size="sm" variant="outline-primary" onClick={() => handleSendCode("Text")} disabled={settingsLoading}>
                  Send Code
                </Button>
              </div>
              <div className="d-flex gap-2">
                <Form.Control
                  value={textCode}
                  onChange={(e) => setTextCode(e.target.value.replace(/\D/g, ""))}
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  disabled={settingsLoading}
                />
                <Button size="sm" onClick={() => handleVerifyCode("Text")} disabled={settingsLoading || textCode.length !== 6}>
                  Verify
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const getBadgeColor = (eventType: string): string => {
  switch (eventType.toLowerCase()) {
    case "test":
      return "info";
    case "activation":
      return "danger";
    case "annual":
      return "warning";
    case "onboarding":
      return "success";
    default:
      return "secondary";
  }
};

export default OobUserHome;
